import React from 'react';
import Constants from 'utils/Constants';
import ProcessService from 'services/ProcessService';
import ProcessUtils from 'utils/ProcessUtils';
import _ from 'lodash';
import moment from 'moment';
import DataExplorerService from 'services/DataExplorerService';
import RunLocation from 'utils/RunLocation';

/**
 * Utility class used to validate different parts of a process
 */
export default class ProcessValidator {
    // Global list of all units that have been validated (so we don't have to validate twice)
    static unitsValidation = {};

    constructor(process) {
        this.process = process;
        this.validationResults = {};
        this.inputDatasetsPath = ['variable_retrieval', 'input_datasets'];
        this.retrievedVariablesPath = ['variable_retrieval', 'retrieved_variables'];
        this.coordinateSystemsPath = ['variable_retrieval', 'coordinate_systems'];
        this.transformParametersPath = ['variable_retrieval', 'transformation_parameters'];

        this.validVariableNamingPattern = /^[\w|_|@]*$/;
        this.validVariableBeginningCharacterPattern = /^[a-zA-Z]/;
    }

    /**
     * Returns any units defined in the processes variables or coordinate systems that have not yet
     * been validated.
     * @param process
     */
    static getUnitsToValidate(process) {
        const unitsToValidate = {};

        const addIfNeeded = (units) => {
            if (ProcessValidator.shouldValidate(units) && !ProcessValidator.unitsValidation.hasOwnProperty(units)) {
                unitsToValidate[units] = true;
            }
        };

        // First check all the retrieved variable units
        ProcessUtils.visitRetrievedVariables(process, retrievedVariable => {
            addIfNeeded(retrievedVariable.units);

            // Also include units from original input datastream variable (in case the retrieved variable changed the units)
            const originalUnitsArray = ProcessUtils.getOriginalVariableUnits(process, retrievedVariable.name);
            originalUnitsArray.forEach(originalUnits => {
                addIfNeeded(originalUnits);
            });
        });

        // Now check all the dimension units
        ProcessUtils.visitDimensions(process, (dimension) => {
            addIfNeeded(dimension.coordinate_variable_units);
        });

        return Object.keys(unitsToValidate);
    }

    static shouldValidate(units) {
        let shouldValidate = false;

        if (units && !Constants.NON_VALIDATED_UNITS.includes(units)) {
            shouldValidate = true;
        }
        return shouldValidate;
    }

    /**
     * Update the cached units validation with a new result
     * @param unit
     * @param isValid
     */
    static addUnitsValidation(unit, isValid) {
        ProcessValidator.unitsValidation[unit] = isValid;
    }

    validateProcess = () => {
        this.validationResults = {};
        this.validateName(['name']);
        // Also validate that the name isn't the same as the wizard default (make sure user changes it)
        if (this.process.name === Constants.PROCESS_WIZARD_NEW_NAME) {
            this.recordError(['name'], Constants.LEVEL_ERROR, 'Each process requires a unique name.');
        }
        this.validatePositiveInteger(['processing_interval'], 'Processing interval');
        this.validateDescription(['description']);
        this.validateCodeUrl();
        this.validateDeprecation();
        this.validateProcessEmail();
        this.validateRunLocations(['run_locations'], this.process);
        this.validateDataLocations(['data_locations'], this.process);
        this.validateDatastreams(['output_datastreams'], 'output datastream', true);
        this.validateDatastreams(['input_datastreams'], 'input datastream', false);

        if (!_.isEmpty(this.process.variable_retrieval)) { // only validate if we have variable retrieval (i.e., VAP)
            this.validateInputDatasets();
            this.validateRetrievedVariables();
            this.validateCoordinateSystems();
            this.validateOutputDatastreamsForVariableRetrieval();
            this.validateTransformationParameters();
        }

        return this.validationResults;
    };

    validateRunParameters(parameters) {
        this.validationResults = {};

        // email
        let results = this.validateEmail(parameters.contact_email);
        if (results !== null) {
            this.recordError(['contact_email'], results.level, results.message);
        }

        // Start date
        results = this.validateRunDate(parameters.start_date);
        if (results !== null) {
            this.recordError(['start_date'], results.level, results.message);
        }

        // End date
        results = this.validateRunDate(parameters.end_date, parameters.start_date);
        if (results !== null) {
            this.recordError(['end_date'], results.level, results.message);
        }
        
        this.validateDataLocations(['data_locations'], parameters);
        this.validateRunLocations(['run_locations'], parameters);
        return this.validationResults;
    }

    /**
     * Gets the worst error for the given field.  For example
     * ['variable_retrieval', 'coordinate_system', 'coord1', 'dimensions', 'time']
     * @param {object} processErrors 
     * @param {string[]} fieldPath 
     */
    static getRollupErrorForField(processErrors, fieldPath) {
        let maxError = null;
        if (processErrors) {
            const errorPathPrefix = fieldPath.join('.');
            const errors = [];
            Object.keys(processErrors).forEach(errorPath => {
                if (errorPath.startsWith(errorPathPrefix)) {
                    errors.push(processErrors[errorPath]);
                }
            });
            maxError = ProcessValidator.getRollupError(errors);
        }
        return maxError;
    }

    /**
     * Gets all errors that apply to a given field and its children (if needed)
     * @param processErrors
     * @param fieldPath
     */
    static getErrorListForField(processErrors, fieldPath, includeChildren) {
        const errorList = [];
        if (processErrors) {
            const errorPathPrefix = fieldPath.join('.');
            const subPathPrefix = `${errorPathPrefix}.`;

            Object.keys(processErrors).forEach(errorPath => {
                if (errorPath === errorPathPrefix || (includeChildren && errorPath.startsWith(subPathPrefix))) {
                    errorList.push(...processErrors[errorPath]);
                }
            });
        }
        return errorList;
    }

    static getWizardStepRollupErrorList(processErrors, stepName, dimensionName) {
        const errors = [];

        if (processErrors) {
            if (stepName === 'info') {
                Object.keys(processErrors).forEach(fieldPath => {
                    const fields = ['name', 'description', 'run_locations', 'notification_email'];
                    if (fields.indexOf(fieldPath) > -1) {
                        errors.push(...processErrors[fieldPath]);
                    }
                });
            } else if (stepName === 'inputs') {
                Object.keys(processErrors).forEach(fieldPath => {
                    const fields = ['input_datastreams', 'variable_retrieval.retrieved_variables'];
                    if (fields.indexOf(fieldPath) > -1) {
                        errors.push(...processErrors[fieldPath]);
                    }
                });
            } else if (stepName === 'outputs') {
                Object.keys(processErrors).forEach(fieldPath => {
                    const path = fieldPath.split(".");
                    if (path[0] === 'output_datastreams') {
                        errors.push(...processErrors[fieldPath]);
                    }
                });
            } else if (stepName === 'dimensions') {

            }

            Object.keys(processErrors).forEach(fieldPath => {

            });
        }

        return errors;
    }

    static getErrorClassForField(processErrors, fieldPath) {
        const maxError = ProcessValidator.getRollupErrorForField(processErrors, fieldPath);
        return ProcessValidator.getErrorClassName(maxError);
    }

    /**
     * Return a new shallow copy of the errors that only includes a field if one error or disallow is present. 
     * Warnings are omitted.
     * @param {object} processErrors 
     */
    static filterWarnings(processErrors) {
        const newErrors = {};
        if (processErrors) {
            Object.keys(processErrors).forEach(errorPath => {
                const newErrorArray = [];
                const errorArray = processErrors[errorPath];
                errorArray.forEach(error => {
                    if (error.level !== Constants.LEVEL_WARNING) {
                        newErrorArray.push(error);
                    }
                });
                if (newErrorArray.length > 0) {
                    newErrors[errorPath] = newErrorArray;
                }
            });
        }
        return newErrors;
    }

    static getRollupError(errors, processName) {
        let maxError = null;
        let currentErrorLevel = -1;
        const ignoredErrors = ProcessUtils.getIgnoredValidation(processName);

        if (errors !== null) { // RIEBase may pass null if no errors defined for an entity
            const evaluateError = (error) => {
                let errorLevelKey = error.level;
                if (ignoredErrors) {
                    errorLevelKey = !!ignoredErrors[error.key] ? Constants.LEVEL_IGNORE : errorLevelKey;
                }

                const errorLevel = Constants.ERROR_LABELS_TO_VALUE[errorLevelKey];

                if (errorLevel > currentErrorLevel) {
                    currentErrorLevel = errorLevel;
                    maxError = { level: errorLevelKey };
                }
            };

            errors.forEach(errorOrArray => {
                if (Array.isArray(errorOrArray)) {
                    errorOrArray.forEach(error => {
                        evaluateError(error);
                    });
                } else {
                    evaluateError(errorOrArray);
                }
            });
        }

        return maxError;
    }

    static getErrorClassName(error) {
        const classNames = [];
        if (error) {
            classNames.push('invalid');
            if (error.level === Constants.LEVEL_ERROR) {
                classNames.push('error');

            } else if (error.level === Constants.LEVEL_WARNING) {
                classNames.push('warning');

            } else if (error.level === Constants.LEVEL_DISALLOW) {
                classNames.push('disallow');
            } else if (error.level === Constants.LEVEL_IGNORE) {
                classNames.push('ignore');
            }
        }

        return classNames.join(' ');
    }

    recordError(fieldPath, errorOrLevel, errorMessage) {
        const pathString = fieldPath.join('.'); // separate array elements with a '.'
        let error = null;

        if (typeof errorOrLevel === 'string') {
            const errorLevel = errorOrLevel;
            error = {
                key: pathString,
                fieldPath: fieldPath,
                level: errorLevel,
                message: errorMessage
            };

        } else {
            error = errorOrLevel;
        }

        let validationArray = this.validationResults[pathString];
        if (!validationArray) {
            this.validationResults[pathString] = [];
            validationArray = this.validationResults[pathString];
        }

        validationArray.push(error);
        return error;
    }

    /**
     * General case to check string format. Names must be at least 2 characters, and
     * can only contain letters, numbers, or underscore.
     */
    validateName = (field, existingList) => {
        const name = _.get(this.process, field);
        const validNamePattern = /^[\w]*$/;

        // combobox clear returns null
        if (name === '' || name === null) {
            this.recordError(field, Constants.LEVEL_ERROR, 'Name cannot be blank.');

        } else if (!validNamePattern.test(name)) {
            this.recordError(field, Constants.LEVEL_ERROR, 'Name may only include letters, numbers, and underscore.');

        } else if (name.length < 2) {
            this.recordError(field, Constants.LEVEL_ERROR, 'Name must be at least two characters long.');

        } else if (existingList) {
            if (!this.isUnique(name, existingList)) {
                this.recordError(field, Constants.LEVEL_ERROR, 'Name already exists.');
            }
        }
    };

    /**
     * Make sure that all rules have a complete set of information and that the names are valid
     * @param inputDatasetName
     */
    validateRetrievalRules(inputDatasetName) {
        const inputDataset = this.process.variable_retrieval.input_datasets[inputDatasetName];
        const retrievedVariables = ProcessUtils.getRetrievedVariablesForDataset(this.process, inputDatasetName);


        inputDataset.rules.forEach((rule, index) => {
            const rulePath = ['variable_retrieval', 'input_datasets', inputDatasetName, 'rules', index];

            // 1) Priority (don't worry about checking this because we won't let users enter an invalid value)
            // 2) Run location (don't worry about checking this because we won't let users enter an invalid value)
            // 3) Start time (don't worry about checking this because we won't let users enter an invalid value)
            // 4) End time - make sure end time is AFTER start time
            const start = rule.run_time.start;
            const end = rule.run_time.end;
            if (end !== 'ANY_TIME' && start !== 'ANY_TIME') {
                if (Number(start) > Number(end)) {
                    const path = [...rulePath, 'run_time', 'end'];
                    this.recordError(path, Constants.LEVEL_ERROR, 'End time must occur after start time.');
                }
            }

            // 5) Data location
            // TODO: also validate that the datastream_name is valid
            const dataLocationPath = [...rulePath, 'data_location'];
            if (!rule.data_location.site) {
                this.recordError(dataLocationPath, Constants.LEVEL_ERROR, 'Site/Facility is required for data location.');

            }

            // 6) Datastream Name
            if (!rule.datastream_name) {
                this.recordError(dataLocationPath, Constants.LEVEL_ERROR, 'Datastream is required for data location.');

            } else {
                const datastreamName = rule.datastream_name;
                const nameResult = this.isDatastreamNameValid(datastreamName);
                if (nameResult.message) {
                    this.recordError(rulePath, nameResult.level, nameResult.message);
                }
            }
            // 7) Variable mappings - make sure there is a value for each variable
            const errorMessage = 'Each retrieval rule must include the name of the variable in the input datastream to retrieve.';
            let areMappingsMissing = retrievedVariables.length === 0;
            retrievedVariables.forEach(retrievedVariableName => {
                const variableMappings = rule.variable_mappings[retrievedVariableName];
                const mappingPath = ['variable_retrieval', 'input_datasets', inputDatasetName, 'rules', index, 'variable_mappings', retrievedVariableName];

                if (!variableMappings || variableMappings.length === 0) {
                    areMappingsMissing = true;
                    this.recordError(mappingPath, Constants.LEVEL_ERROR, errorMessage);
                } else {
                    if (rule.datastream_name) {
                        let shouldInclude = [];
                        const dodVars = ProcessUtils.getDatastreamVariablesForDod(rule.datastream_name);
                        variableMappings.forEach(dsVar => {
                            if (dodVars && dodVars[dsVar]) {
                                const priorNames = dodVars[dsVar].renamed;
                                if (priorNames && priorNames.length > 0) {
                                    shouldInclude = shouldInclude.concat(priorNames);
                                }
                            }
                        });

                        const warningList = [];
                        shouldInclude.forEach(dsVar => {
                            if (variableMappings.indexOf(dsVar) == -1) {
                                warningList.push(dsVar);
                            }
                        });
                        if (warningList.length > 0) {
                            this.recordError(mappingPath, Constants.LEVEL_WARNING, `Variable has been renamed and should include the following additional names in its mapping: ${warningList.join(', ')}`)
                        }

                    }
                }
            });
            if (areMappingsMissing) {
                // roll up the error to the rule
                this.recordError(rulePath, Constants.LEVEL_ERROR, `${errorMessage}  Please specify in last column of this table.`);
            }

        });

        // Validate special rule cases, such as running at SGP C1 and using met.b1 data.
        this.validateSGPC1Rules(inputDatasetName);
    }

    validateSGPC1Rules(inputDatasetName) {
        const sgpDatastreamsWithMissingC1Data = Constants.SGP_DATASTREAMS_WITH_NO_C1_DATA;

        const inputDataset = this.process.variable_retrieval.input_datasets[inputDatasetName];

        const sgpC1 = new RunLocation('SGP', 'C1');
        const sgpE13 = new RunLocation('SGP', 'E13');

        if (ProcessUtils.hasRunLocation(this.process, sgpC1)) {
            let hasMetB1 = false;
            let hasE13 = false;
            let metB1Rule = -1;
            let e13Rule = -1;

            let missingDatastreamName = null;
            inputDataset.rules.forEach((rule, index) => {
                if (sgpDatastreamsWithMissingC1Data.indexOf(rule.datastream_name) > -1) {
                    hasMetB1 = true;
                    missingDatastreamName = rule.datastream_name;

                    if (sgpC1.isEqual(rule.run_location) && sgpE13.isEqual(rule.data_location)) {
                        hasE13 = true;
                        e13Rule = index;

                    } else {
                        metB1Rule = index;
                    }
                }
            });

            if (hasMetB1 && !hasE13) {
                // The input dataset is invalid because it doesn't account for the fact that there is no met.b1
                // data at SGP C1
                const inputDatasetRulesPath = [...this.inputDatasetsPath, inputDatasetName, 'rules', metB1Rule];
                this.recordError(inputDatasetRulesPath, Constants.LEVEL_WARNING, `No ${missingDatastreamName} data is available at SGP C1`);

            } else if (hasMetB1 && hasE13 && e13Rule > metB1Rule) {
                // The SGP E13 rule must go first in the priority since it is special
                const inputDatasetRulesPath = [...this.inputDatasetsPath, inputDatasetName, 'rules', e13Rule];
                this.recordError(inputDatasetRulesPath, Constants.LEVEL_WARNING, `The SGP E13 rule must have a higher priority than the general ${missingDatastreamName} rule.`);
            }
        }
    }

    validatePositiveInteger(field, label) {
        const value = _.get(this.process, field);

        if (this.isPositiveInteger(value)) {
            this.recordError(field, Constants.LEVEL_WARNING, `${label} must be a positive integer.`);
        }
    }

    validateDescription = (field) => {
        const description = _.get(this.process, field);

        if (description && description.length > 500) {
            this.recordError(field, Constants.LEVEL_ERROR, 'Description must be less than 500 characters.');
        }
    };

    validateCodeUrl = () => {
        let urlMissing = false;
        if (this.process.ui_state) {
            const codeUrl = this.process.ui_state.code_url;
            if (_.isEmpty(codeUrl) || !codeUrl.toLowerCase().startsWith('https://code.arm.gov')) {
                urlMissing = true;
            }
        } else {
            urlMissing = true;
        }
        if (urlMissing) {
            this.recordError(['ui_state', 'code_url'], Constants.LEVEL_WARNING,
                'It is recommended that all code is stored at https://code.arm.gov.');
        }
    };

    validateProcessEmail = () => {
        const email = this.process.notification_email;

        if (_.isEmpty(email)) {
            // if the warning or error checkbox checked but no email address specified, this is an error
            if (this.process.email_errors || this.process.email_warnings) {
                this.recordError(['notification_email'], Constants.LEVEL_WARNING, 'Email address is required to send notification messages.');
            }

        }

        const results = this.validateEmail(email);
        if (results !== null) {
            this.recordError(['notification_email'], results.level, results.message);
        }
    };

    validateDeprecation = () => {

        if (this.process.deprecated && _.isEmpty(this.process.deprecated_text)) {
            this.recordError(['deprecated_text'], Constants.LEVEL_WARNING, 'Reason for deprecating process is required.');
        }
    }

    validateRunLocations = (field, data) => {
        const runLocations = _.get(data, field);

        if (runLocations.length === 0) {
            this.recordError(field, Constants.LEVEL_ERROR, 'At least one run location is required.');
        }
    };

    validateDataLocations = (field, data) => {
   
        const dataLocations = _.get(data, field);
        const message = 'At least one input file location must be specified.';

        // Only validate if a dataLocations object exist.  For old processes, this will be null.
        if (dataLocations) {
            if (!dataLocations.use_user && !dataLocations.use_archive && !dataLocations.use_test) {
                this.recordError(field, Constants.LEVEL_ERROR, message);
            }
            if (dataLocations.use_user && !dataLocations.user_folder) {
                const fieldPath = field.concat(['user_folder']);
                this.recordError(fieldPath, Constants.LEVEL_ERROR, 'User data folder must be specified.');
            }
        
        } 
    };

    validateDatastreams(fieldPath, label, includeNameInPath) {
        const datastreams = _.get(this.process, fieldPath);
        if (!datastreams || datastreams.length === 0) {
            this.recordError(fieldPath, Constants.LEVEL_ERROR, `At least one ${label} must be defined.`);

        } else {
            this.validateDatastreamNames(fieldPath, includeNameInPath);
        }
    }

    validateDatastreamNames = (field, includeNameInPath) => {
        const names = _.get(this.process, field);

        const recordDatastreamNameError = (array, message) => {
            this.recordError(field, Constants.LEVEL_ERROR, message);
            if (includeNameInPath) {
                array.forEach(name => {
                    this.recordError([...field, name], Constants.LEVEL_ERROR, message);
                });
            }
        };

        const errors = this.getDatastreamNameErrors(names);

        if (errors.blank.names.length > 0) {
            recordDatastreamNameError(errors.blank.names, errors.blank.message);
        }
        if (errors.badFormat.names.length > 0) {
            recordDatastreamNameError(errors.badFormat.names, errors.badFormat.message);
        }
        if (errors.wrongCharacters.names.length > 0) {
            recordDatastreamNameError(errors.wrongCharacters.names, errors.wrongCharacters.message);
        }
        if (errors.tooLong.names.length > 0) {
            recordDatastreamNameError(errors.tooLong.names, errors.tooLong.message);
        }
        if (errors.badLevel.names.length > 0) {
            recordDatastreamNameError(errors.badLevel.names, errors.badLevel.message);
        }
    };

    validateGridParameter = (field, requireNonzero, requirePositive) => {
        const value = _.get(this.process, field);

        if (isNaN(value) || !isFinite(value)) {
            this.recordError(field, Constants.LEVEL_ERROR, 'Entered value is not an integer.');

        } else if (requireNonzero && requirePositive && value <= 0) {
            this.recordError(field, Constants.LEVEL_ERROR, `${field[field.length - 1]} value must be > 0`);
        } else if (requireNonzero && value === 0) {
            this.recordError(field, Constants.LEVEL_ERROR, `${field[field.length - 1]} value must not be 0`);
        }
    };

    isEntityNameValid = (name, existingList, label) => {
        let result = { level: null, message: '' };
        const validNamePattern = /^[\w]*$/;

        //combobox clear returns null
        if (name === '' || name === null) {
            result = { level: Constants.LEVEL_ERROR, message: `${label} cannot be blank.` };
        } else if (!validNamePattern.test(name)) {
            result = { level: Constants.LEVEL_ERROR, message: `${label} may only include letters, numbers, and underscore.` };
        } else if (name.length < 2) {
            result = { level: Constants.LEVEL_ERROR, message: `${label} must be at least two characters long.` };
        } else if (existingList) {
            if (!this.isUnique(name, existingList)) {
                result = { level: Constants.LEVEL_ERROR, message: `${label} already exists.` };
            }
        }
        return result;
    };

    isVariablePrefixValid = (prefix) => {
        let result = { level: null, message: '' };

        if (prefix !== '' && prefix !== null) {
            if (!this.validVariableNamingPattern.test(prefix)) {
                result.level = Constants.LEVEL_ERROR;
                result.message = 'Prefix may only include letters, numbers, underscores, and @s.';

            } else if (!prefix.match(this.validVariableBeginningCharacterPattern)) {
                result.level = Constants.LEVEL_ERROR;
                result.message = 'Prefix must begin with a letter.';
            }
        }

        return result;
    };

    isEntityNull(value, label) {
        let result = { level: null, message: '' };

        if (value === '' || value === null) {
            result = { level: Constants.LEVEL_ERROR, message: `${label} cannot be blank.` };
        }

        return result;
    }

    validateRunDate(dateStr, priorDateStr) {
        // 20171005.000000
        //const datePattern = /^(\d{4})(\d{2})(\d{2}).(\d{2})(\d{2})(\d{2})$/;
        let result = null;
        // check that the date format matches that required by ADI
        // Use regex to convert to timestamp
        const dateMoment = moment(dateStr, 'YYYYMMDD.HHmmss');
        const priorDateMoment = moment(priorDateStr, 'YYYYMMDD.HHmmss');

        if (!dateMoment.isValid()) {
            result = { level: Constants.LEVEL_ERROR, message: 'Date is not valid. Allowed format: YYYYMMDD.[hh[mm[ss]]]' }; 
        
        } else if (dateMoment.isValid() && priorDateMoment.isValid()) {
            const date = new Date(dateMoment);
            const priorDate = new Date(priorDateMoment);
            
            // we have to make sure that date comes after prior date
            // use regex to convert to timestamp, then see if it is after
            if (date.getTime() <= priorDate.getTime()) {
                result = { level: Constants.LEVEL_ERROR, message: 'End date must come after start date.' }; 
            }
        }
        return result;
    }

    validateEmail = (email) => {
        let result = null;

        // eslint-disable-next-line
        const validEmailPattern = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (email.length > 0 && !validEmailPattern.test(email)) {
            result = { level: Constants.LEVEL_WARNING, message: 'Email is not valid format.' };
        }

        return result;
    };

    getDatastreamNameErrors = (names) => {
        const blank = [];
        const badFormat = [];
        const wrongCharacters = [];
        const tooLong = [];
        const badLevel = [];

        const getErrorMessage = (msgBefore, msgAfter, nameList) => {
            const nameString = `[${nameList.join(', ')}]`;
            return `${msgBefore} ${nameString} ${msgAfter}`;
        };

        names.forEach(name => {
            if (name === '' || name === null) {
                blank.push(name);
            } else {
                const nameParts = name.split('.');
                if (nameParts.length !== 2) {
                    badFormat.push(name);
                } else {
                    const className = nameParts[0];
                    const levelName = nameParts[1];

                    if (!className.match(/^[\da-z]*$/i)) {
                        wrongCharacters.push(name);

                    } else if (className.length > 24) {
                        tooLong.push(name);

                    } else if (!levelName.match(/^[\dabcsm0]\d$/)) {
                        badLevel.push(name);
                    }
                }
            }
        });

        return ({
            blank: {
                message: 'Datastream cannot be blank',
                names: blank
            },
            badFormat: {
                message: getErrorMessage('Datastream names', 'must contain classname AND level (e.g., 05okm.a1)', badFormat),
                names: badFormat
            },
            wrongCharacters: {
                message: getErrorMessage('Datastream class names', 'may only contain letters and numbers', wrongCharacters),
                names: wrongCharacters
            },
            tooLong: {
                message: getErrorMessage('Datastream class names', 'cannot exceed 24 characters', tooLong),
                names: tooLong
            },
            badLevel: {
                message: getErrorMessage('Datastream level', 'must be 0, a, b, c, s, or m followed by a number.', badLevel),
                names: badLevel
            }
        });
    };

    isDatastreamNameValid = (name, existingList) => {
        const result = { level: null, message: null };

        const errors = this.getDatastreamNameErrors([name]);

        if (errors.blank.names.length > 0) {
            result.level = Constants.LEVEL_ERROR;
            result.message = errors.blank.message;
        }
        if (errors.badFormat.names.length > 0) {
            result.level = Constants.LEVEL_ERROR;
            result.message = errors.badFormat.message;
        }
        if (errors.wrongCharacters.names.length > 0) {
            result.level = Constants.LEVEL_ERROR;
            result.message = errors.wrongCharacters.message;
        }
        if (errors.tooLong.names.length > 0) {
            result.level = Constants.LEVEL_ERROR;
            result.message = errors.tooLong.message;
        }
        if (errors.badLevel.names.length > 0) {
            result.level = Constants.LEVEL_ERROR;
            result.message = errors.badLevel.message;
        }

        // also check for uniqueness
        if (result.level === null && existingList) {
            if (!this.isUnique(name, existingList)) {
                result.level = Constants.LEVEL_ERROR;
                result.message = 'A datastream already exists with that name.';
            }
        }

        return result;
    };

    /**
     * Generic check to make sure a string has a value that is not whitespace
     */
    isStringEmpty(value) {
        let isEmpty = false;

        if (value === '' || value === null || typeof value === 'undefined' || value.trim() === '') {
            isEmpty = true;
        }

        return isEmpty;
    }

    isUnitsValid(units) {
        const result = { level: null, message: null };
        if (this.isStringEmpty(units)) {
            result.level = Constants.LEVEL_WARNING;
            result.message = 'Units are required.';

        } else {
            if (ProcessValidator.shouldValidate(units) && ProcessValidator.unitsValidation.hasOwnProperty(units)) {
                // Check udunits validation
                const validation = ProcessValidator.unitsValidation[units];
                if (!validation) {
                    result.level = Constants.LEVEL_WARNING;
                    result.message = 'Invalid units.';

                }
            }
        }

        return result;
    }

    isPositiveInteger(value) {
        let isPositiveInteger = false;
        if (`${value}`.length > 0 && (isNaN(value) || !isFinite(value) || parseInt(value, 10) < 0)) {
            isPositiveInteger = true;
        }

        return isPositiveInteger;
    }

    isOutputVariableNameValid = (name, existingList) => {
        const result = { level: null, message: null };
        const validNamePattern = /^[\w]*$/;

        if (name === '' || name === null) {
            result.level = Constants.LEVEL_WARNING;
            result.message = 'If no output variable is provided, variables will have to be mapped in code.';
        } else if (!validNamePattern.test(name)) {
            result.level = Constants.LEVEL_ERROR;
            result.message = 'Name may only include letters, numbers, and underscore.';
        } else if (name.length < 2) {
            result.level = Constants.LEVEL_ERROR;
            result.message = 'Name must be at least two characters long.';
        } else if (existingList) {
            if (!this.isUnique(name, existingList)) {
                result.level = Constants.LEVEL_WARNING;
                result.message = 'An output datastream variable with this name is used in another mapping.';
            }
        }

        return result;
    };

    isRetrievedVariableNameValid = (name, existingList) => {
        const result = { level: null, message: null };

        if (name === '' || name === null) {
            result.level = Constants.LEVEL_ERROR;
            result.message = 'Name cannot be blank.';

        } else if (!this.validVariableNamingPattern.test(name)) {
            result.level = Constants.LEVEL_ERROR;
            result.message = 'Name may only include letters, numbers, underscores, and @s.';

        } else if (!name.match(this.validVariableBeginningCharacterPattern)) {
            result.level = Constants.LEVEL_ERROR;
            result.message = 'Name must begin with a letter.';

        } else if (name.length < 2) {
            result.level = Constants.LEVEL_ERROR;
            result.message = 'Name must be at least two characters long';

        } else if (existingList) {
            if (!this.isUnique(name, existingList)) {
                result.level = Constants.LEVEL_ERROR;
                result.message = 'A retrieved variable with this name already exists';
            }
        }

        return result;
    };

    isUnique(value, existingList) {
        let unique = true;
        const indexes = [];
        for (let i = 0; i < existingList.length; i++) {
            if (existingList[i] === value) {
                indexes.push(i);
            }
        }
        if (indexes.length > 1) {
            unique = false;
        }
        return unique;
    }

    validateInputDatasets() {

        const inputDatasets = _.get(this.process, this.inputDatasetsPath);

        if (inputDatasets && Object.keys(inputDatasets).length > 0) {

            Object.keys(inputDatasets).forEach(inputDatasetName => {
                const nameResult = this.isEntityNameValid(inputDatasetName, null, 'Rule set name');
                if (nameResult.level !== null) {
                    const inputDatasetNamePath = [...this.inputDatasetsPath, inputDatasetName, 'name'];
                    this.recordError(inputDatasetNamePath, nameResult.level, nameResult.message);
                }

                this.validatePositiveInteger([...this.inputDatasetsPath, inputDatasetName, 'offsets', 0], 'Offset');
                this.validatePositiveInteger([...this.inputDatasetsPath, inputDatasetName, 'offsets', 1], 'Offset');

                // Make sure the rules are complete
                this.validateRetrievalRules(inputDatasetName);
            });

        } else {
            this.recordError(this.inputDatasetsPath, Constants.LEVEL_ERROR, 'At least one input dataset must be defined.');
        }
    }

    validateRetrievedVariables() {
        const retrievedVariables = _.get(this.process, this.retrievedVariablesPath);

        if (retrievedVariables && Object.keys(retrievedVariables).length > 0) {
            const existingList = Object.keys(retrievedVariables);

            Object.keys(retrievedVariables).forEach(retrievedVariableName => {
                const retrievedVariablePath = [...this.retrievedVariablesPath, retrievedVariableName];
                const retrievedVariable = this.process.variable_retrieval.retrieved_variables[retrievedVariableName];
                const nameResult = this.isRetrievedVariableNameValid(retrievedVariableName, existingList);

                // 1) validate name
                if (nameResult.level !== null) {
                    const retrievedVariableNamePath = [...retrievedVariablePath, 'name'];
                    this.recordError(retrievedVariableNamePath, nameResult.level, nameResult.message);
                }

                // 2) Validate that input dataset is assigned
                if (this.isStringEmpty(retrievedVariable.input_dataset)) {
                    this.recordError([...retrievedVariablePath, 'input_dataset'], Constants.LEVEL_ERROR, 'No retrieval rules defined.');

                } else if (!this.process.variable_retrieval.input_datasets[retrievedVariable.input_dataset]) {
                    this.recordError([...retrievedVariablePath, 'input_dataset'], Constants.LEVEL_ERROR, 'No retrieval rules defined.');
                }

                // 3) Validate that assigned coordinate system exists in the process
                if (retrievedVariable.coordinate_system && !this.process.variable_retrieval.coordinate_systems[retrievedVariable.coordinate_system]) {
                    this.recordError([...retrievedVariablePath, 'coordinate_system'], Constants.LEVEL_ERROR,
                        'Specified coordinate system does not exist.');

                } else if (retrievedVariable.coordinate_system) {
                    // Validate that passhthrough is set correctly
                    retrievedVariable.dims.forEach(dimName => {
                        const dimension = ProcessUtils.getDimension(this.process, retrievedVariable.coordinate_system, dimName);

                        // Dimension might not exist if user gets the process in a bad state (e.g., via text editor)
                        if (dimension) {
                            const variableTransformationType = ProcessUtils.getVariableTransformationType(this.process, retrievedVariableName, dimName);

                            // All variables coming from the same dataset as its coordinate system should be passthrough
                            if (dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING &&
                                dimension.input_dataset === retrievedVariable.input_dataset &&
                                variableTransformationType !== Constants.TRANSFORM_TYPE_PASSTHROUGH) {

                                this.recordError([...retrievedVariablePath, 'coordinate_system'], Constants.LEVEL_WARNING,
                                    `${retrievedVariableName} [${dimName}] comes from the same dataset as its coordinate system, so it should not be transformed (i.e., set the variable transformation type to PASSTHROUGH).`);
                            }
                            // All variables that do not come from the same dataset as its coordinate system should not be passthrough
                            if (dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING &&
                                dimension.input_dataset !== retrievedVariable.input_dataset &&
                                variableTransformationType === Constants.TRANSFORM_TYPE_PASSTHROUGH) {

                                this.recordError([...retrievedVariablePath, 'coordinate_system'], Constants.LEVEL_WARNING,
                                    `${retrievedVariableName} [${dimName}] comes from a different dataset as its coordinate system, so it should use a transformation (i.e., do NOT set the variable transformation type to PASSTHROUGH).`);
                            }

                        }
                    });
                }

                // 4) Validate that output datastream is defined if variable has been assigned a coordinate system
                let foundOutputDatastream = false;
                Object.values(this.process.variable_retrieval.output_datastream_variable_mappings).forEach(mappedVariables => {
                    if (_.includes(Object.keys(mappedVariables), retrievedVariableName)) {
                        foundOutputDatastream = true;
                    }
                });
                if (!foundOutputDatastream && retrievedVariable.coordinate_system) {
                    this.recordError([...retrievedVariablePath, 'output_datastreams'], Constants.LEVEL_WARNING,
                        'No output datastream defined. Are you sure you want to exclude this variable from the output?');
                }

                // 5) Validate units
                let validationResult = this.isUnitsValid(retrievedVariable.units);
                if (validationResult.level !== null) {
                    this.recordError([...retrievedVariablePath, 'units'], validationResult.level, validationResult.message);

                } else {
                    // If the units are valid, we also need to check if the units of the retrieved ariable are different from
                    // the units in the input datastream.  If they are, then we also need to check if the units
                    // from the original input datastream are valid.  If they are not, then we need to show a warning
                    // telling the user that the process will fail because the original units are invalid, so they
                    // cannot be converted.
                    const originalUnitsArray = ProcessUtils.getOriginalVariableUnits(this.process, retrievedVariable.name);
                    originalUnitsArray.forEach(originalUnits => {
                        if (retrievedVariable.units !== originalUnits) {
                            validationResult = this.isUnitsValid(originalUnits);
                            if (validationResult.level !== null) {
                                const message = 'This variable is populated by an input datastream variable with INVALID units.  Therefore this process will fail because the units will not be able to be converted.';
                                this.recordError([...retrievedVariablePath, 'units'], Constants.LEVEL_WARNING, message);
                            }
                        }
                    });
                }


                // 6) Validate that if a coordinate system is assigned, the variable must have time as the first dimension
                // (I don't think this is correct - the check should go on the coordinate system?
                //                if (!this.isStringEmpty(retrievedVariable.coordinate_system) && retrievedVariable.coordinate_system !== Constants.SAME_AS_INPUT_DATASTREAM &&
                //                    retrievedVariable.dims && retrievedVariable.dims.length > 0 && retrievedVariable.dims[0] !== 'time') {
                //                    this.recordError([...retrievedVariablePath, 'coordinate_system'], Constants.LEVEL_ERROR,
                //                        'Time must be the first dimension.');
                //                }

            });

        } else {
            this.recordError(this.retrievedVariablesPath, Constants.LEVEL_WARNING, 'At least one variable must be defined.');
        }
    }

    validateCoordinateSystems() {
        const coordinateSystems = _.get(this.process, this.coordinateSystemsPath);
        if (coordinateSystems && Object.keys(coordinateSystems).length > 0) {

            Object.keys(coordinateSystems).forEach(coordinateSystemName => {
                const coordinateSystem = this.process.variable_retrieval.coordinate_systems[coordinateSystemName];
                const coordinateSystemPath = [...this.coordinateSystemsPath, coordinateSystemName];

                // 1) Validate Name
                this.validateName([...coordinateSystemPath, 'name']);

                // 2) Validate dimensions
                const dimensionNames = Object.keys(coordinateSystems[coordinateSystemName].dimensions);
                if (dimensionNames.length === 0) {
                    this.recordError(coordinateSystemPath, Constants.LEVEL_ERROR, 'All coordinate systems must have at least one dimension.');
                }

                dimensionNames.forEach(dimensionName => {
                    const dimension = coordinateSystem.dimensions[dimensionName];
                    const dimensionPath = [...coordinateSystemPath, 'dimensions', dimensionName];

                    // 2a) Validate dimension name
                    this.validateName([...dimensionPath, 'name'], dimensionNames);

                    // 2b) Validate dimension data type (ignoring for now since user can't enter an invalid value)
                    // 2c) Validate dimension units
                    let validationResult = this.isUnitsValid(dimension.coordinate_variable_units);
                    if (validationResult.level !== null) {
                        this.recordError([...dimensionPath, 'coordinate_variable_units'], validationResult.level, validationResult.message);
                    }

                    // 2d) Validate Grid Shape
                    // Gridding Method (ignore for now since user can't set an invalid value)

                    if (dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_REGULAR_GRID) { // regular_grid
                        this.validateGridParameter([...dimensionPath, 'data_transformation', 'interval'], true);
                        this.validateGridParameter([...dimensionPath, 'data_transformation', 'start']);
                        this.validateGridParameter([...dimensionPath, 'data_transformation', 'end']);
                        this.validateGridParameter([...dimensionPath, 'data_transformation', 'length'], true, true);

                    } else if (dimension.gridding_method === Constants.COORDINATE_SYSTEM_GRIDDING_METHOD_MAPPING) { // mapping

                        // 1)Validate that there is an input dataset defined
                        if (this.isStringEmpty(dimension.input_dataset)) {
                            this.recordError([...dimensionPath, 'input_dataset'], Constants.LEVEL_ERROR, 'Datatstream is required.');

                        } else {
                            // Check that the input dataset exists in the process
                            const inputDatasetName = dimension.input_dataset;
                            if (!this.process.variable_retrieval.input_datasets[inputDatasetName]) {
                                this.recordError([...dimensionPath, 'input_dataset'], Constants.LEVEL_ERROR, 'Datastream is not included in a retrieval rule.');

                            } else {
                                const inputDataset = this.process.variable_retrieval.input_datasets[inputDatasetName];

                                // Make sure there is a coordinate variable mapping assigned for each rule in the input dataset
                                const coordinateVariablesPerRule = dimension.coordinate_variables_per_rule;
                                if (_.isEmpty(coordinateVariablesPerRule)) {
                                    this.recordError([...dimensionPath, 'coordinate_variables_per_rule'], Constants.LEVEL_ERROR, 'Coordinate variable is required if variable of same name as dimension exists in DOD definition.');

                                } else {
                                    coordinateVariablesPerRule.forEach(variablesArray => {
                                        if (variablesArray.length === 0 || _.isEmpty(variablesArray[0].trim())) {
                                            this.recordError([...dimensionPath, 'coordinate_variables_per_rule'], Constants.LEVEL_ERROR, 'Coordinate variable is required if variable of same name as dimension exists in DOD definition.');
                                        }
                                    });


                                    if (coordinateVariablesPerRule.length !== inputDataset.rules.length) {
                                        this.recordError([...dimensionPath, 'coordinate_variables_per_rule'], Constants.LEVEL_ERROR,
                                            `Mapped coordinate variables must be defined for every rule in the mapped dataset.  (See ret->shapes->${coordinateSystemName}->vars in the text view.)`);
                                    }
                                }

                                if (inputDataset.rules.length > 1) {
                                    let sites = [];
                                    inputDataset.rules.forEach(rule => {
                                        let site = rule.data_location.site;
                                        site = rule.data_location.fac ? `${site} ${rule.data_location.fac }` : site;
                                        sites.push(site);
                                    });

                                    const uniques = (arr) => _.xor(...arr.map(a => [a]));
                                    const duplicates = (arr) => _.xor(arr, uniques(arr));

                                    const duplicateSites = duplicates(sites);
                                    duplicateSites.forEach(siteDup => {
                                        const datastreams = [];
                                        inputDataset.rules.forEach(rule => {
                                            let site = rule.data_location.site;
                                            site = rule.data_location.fac ? `${site} ${rule.data_location.fac }` : site;

                                            if (site === siteDup) {
                                                datastreams.push(rule.datastream_name);
                                            }
                                        });

                                        if (datastreams.length === _.uniq(datastreams).length) {
                                            this.recordError([...dimensionPath, 'input_dataset'], Constants.LEVEL_WARNING,
                                                'The retrieval group for which the coordinate dimension is defined has more than one possible input source.  As such the output grid shape is based on multiple datastreams that could produce variations in output shape day to day.')
                                        }

                                    });
                                }
                            }
                        }

                        // 2) Validate Transformation

                        if (dimensionName !== 'time') {

                            // If there are more than one dataset going into this dimension, then the dimension should not
                            // be passthrough.  If it is, then show a warning.
                            const inputDatasetsWithSameDimension =
                                ProcessUtils.getInputDatasetsForCoordSysDimension(this.process, coordinateSystemName, dimensionName);

                            if (inputDatasetsWithSameDimension.length > 1 &&
                                dimension.data_transformation.transformation_type === Constants.TRANSFORM_TYPE_PASSTHROUGH) {
                                this.recordError([...dimensionPath, 'data_transformation', 'transformation_type'],
                                    Constants.LEVEL_WARNING, 'Dimension is populated by variables from more than one dataset, so it should not use PASSTHROUGH.');
                            }
                        }
                    }
                });
            });


        }
    }

    validateTransformationParameters = () => {
        ProcessUtils.visitTransformationParameters(this.process, (parameter) => {
            const parameterName = parameter.name;
            ProcessUtils.visitTransformationCoordinateSystemDefaults(this.process, parameterName, (coordinateObj) => {
                const coordinateSystemName = coordinateObj.name;
                const dimensionName = coordinateObj.dim;
                const defaultValue = coordinateObj.default_value;
                const baseFieldPath = [...this.transformParametersPath, parameterName, coordinateSystemName, dimensionName];

                switch (parameterName) {
                    case 'width':
                        if (typeof defaultValue !== 'undefined') {
                            this.validateBinWidth([...baseFieldPath, 'default_value'], coordinateSystemName, dimensionName, defaultValue);
                        }
                        break;
                    default:
                        break;
                }
            });

            ProcessUtils.visitTransformationVariables(this.process, parameterName, (variableObj) => {
                const variableName = variableObj.name;
                const value = variableObj.value;
                const coordinateSystemName = ProcessUtils.getTransformVariableCoordinateSystem(this.process, parameterName, variableObj.name);;
                const dimensionName = variableObj.dim;
                const inputDatastream = variableObj.input_datastream;
                const baseFieldPath = ProcessUtils.getTransformOverrideVariableFieldPath(parameterName, coordinateSystemName, dimensionName, inputDatastream, variableName);

                switch (parameterName) {
                    case 'width':
                        this.validateBinWidth(baseFieldPath, coordinateSystemName, dimensionName, value);
                        break;
                    case 'qc_mask':
                    case 'qc_bad':
                        if (!_.isEmpty(DataExplorerService.getCurrentData(this.process.name, inputDatastream))) {
                            // Only validate if explorer data is loaded
                            this.validateQCParameter(baseFieldPath, inputDatastream, variableName, parameterName, value);
                        }
                        break;
                    case 'std_ind_max':
                    case 'std_bad_max':
                        if (!value) {
                            this.recordError(baseFieldPath, Constants.LEVEL_ERROR, 'Standard deviation threshold cannot be empty.');
                        }
                        break;
                    case 'goodfrac_bad_min':
                    case 'goodfrac_ind_min':
                        if (value < 0 || value > 1) {
                            this.recordError(baseFieldPath, Constants.LEVEL_ERROR, 'Value must be between 0 and 1.');
                        }
                        break;
                    default:
                        break;
                }
            });
        });
    };

    validateBinWidth = (fieldPath, coordinateSystemName, dimensionName, value) => {

        if (!value && value !== 0) {
            this.recordError(fieldPath, Constants.LEVEL_ERROR, "Bin width can't be empty.");
        } else {
            const ttype = ProcessUtils.getDimensionTransformationType(this.process, coordinateSystemName, dimensionName);

            // only check this if we aren't using nearest neighbor or passthrough
            if (ttype !== Constants.TRANSFORM_TYPE_PASSTHROUGH && ttype !== Constants.TRANSFORM_TYPE_SUBSAMPLE) {
                const dimensionInterval = ProcessUtils.getDimensionInterval(this.process, coordinateSystemName, dimensionName);
                if (dimensionInterval !== null && value < dimensionInterval) {
                    this.recordError(fieldPath, Constants.LEVEL_WARNING, "Bin width shouldn't be less than interval.");
                }
            }
        }

    };

    validateQCParameter(baseFieldPath, inputDatastream, variableName, parameterName, value) {
        // Sometimes the value is passed as an array of strings, and sometimes it's passed as an array of ints
        // We have to convert to strings so array.indexOf will work correctly
        value = value.map(currentValue => {
            return currentValue.toString();
        });
        const type = parameterName === 'qc_mask' ? 'bit' : 'integer';
        const badValues = ProcessUtils.getQCBits(this.process, inputDatastream, variableName, 'bad', type);

        let missingBadValues = [];
        badValues.forEach(v => {
            if (value.indexOf(v) === -1) {
                missingBadValues.push(v);
            }
        });

        const indValues = ProcessUtils.getQCBits(this.process, inputDatastream, variableName, 'indeterminate', type);
        const options = badValues.concat(indValues);

        const notIndValues = [];
        value.forEach(v => {
            if (options.indexOf(v) === -1) {
                notIndValues.push(v);
            }
        });

        if (missingBadValues.length > 0) {
            this.recordError(baseFieldPath, Constants.LEVEL_WARNING, `Bad QC ${type}${missingBadValues.length > 1 ? 's' : ''} (${missingBadValues.join(', ')}) must be included because they are defined in the DOD.`);

        } else if (notIndValues.length > 0) {
            this.recordError(baseFieldPath, Constants.LEVEL_WARNING, `QC ${type}${notIndValues.length > 1 ? 's' : ''} (${notIndValues.join(', ')}) may not be included because they have not been defined in the DOD.`);
        }
    }

    /**
     * Validates output datasteams for the purposes of variable retrieval (an addition to the check performed by
     * validateDatastreams).
     */
    validateOutputDatastreamsForVariableRetrieval() {

        this.process.output_datastreams.forEach(outputDatastreamName => {
            const outputDatastreamErrors = []; // for rollup errors on the datastream

            // Make sure the variable mappings for that datastream are valid
            const datastreamVariableMappings = this.process.variable_retrieval.output_datastream_variable_mappings[outputDatastreamName];
            if ((datastreamVariableMappings && Object.keys(datastreamVariableMappings).length === 0) || !datastreamVariableMappings) {
                this.recordError(['output_datastreams', outputDatastreamName], Constants.LEVEL_WARNING, 'No variables mapped for output datastream');
            }

            if (datastreamVariableMappings) {
                const coordinateSystems = {};
                Object.keys(datastreamVariableMappings).forEach(retrievedVariableName => {
                    const datastreamVariableName = datastreamVariableMappings[retrievedVariableName];
                    const validationResult = this.isOutputVariableNameValid(datastreamVariableName, Object.values(datastreamVariableMappings));

                    if (validationResult.level !== null) {
                        const path = ['variable_retrieval', 'output_datastream_variable_mappings', outputDatastreamName, retrievedVariableName];
                        const error = this.recordError(path, validationResult.level, validationResult.message);
                        outputDatastreamErrors.push(error);
                    }

                    // Also need to check coordinate system
                    const retrievedVariable = this.process.variable_retrieval.retrieved_variables[retrievedVariableName];
                    const coordSysName = retrievedVariable.coordinate_system;
                    if (coordSysName) {
                        coordinateSystems[coordSysName] = '';
                    }
                });
                if (Object.keys(coordinateSystems).length > 1) {
                    const msg = `This output datastream is mapped to multiple coordinate systems (${Object.keys(coordinateSystems).join(', ')}).  All variables in this datastream MUST use the same coordinate system.`;
                    this.recordError(['output_datastreams', outputDatastreamName], Constants.LEVEL_ERROR, msg);
                }
            }
        });

        // TODO: if there is a mapping and the process.output_datastreams array does not contain that output datastream,
        // is this an error that should be reported?

    }

    setDefaultDeleteValidationResults = () => {
        this.validationResults = { canDelete: true, message: '' };
    };

    canInputDatasetsBeDeleted = (selectedItems) => {
        this.setDefaultDeleteValidationResults();
        let linkedVariables = [];
        let linkedCoordinateSystems = [];
        let datastreamList = [];

        // first see if there are variables or coordinate systems mapped to this input dataset
        Object.keys(selectedItems).forEach(inputDatasetName => {
            linkedVariables = _.union(linkedVariables, ProcessUtils.getRetrievedVariablesForDataset(this.process, inputDatasetName));
            linkedCoordinateSystems = _.union(linkedCoordinateSystems, ProcessUtils.getCoordinateSystemsMappedToDataset(this.process, inputDatasetName));
            datastreamList = _.union(datastreamList, ProcessUtils.getInputDatastreamsForDataset(this.process, inputDatasetName));
        });

        const datastreamItems = datastreamList.map(datastreamName => {
            return <li key={datastreamName}>{datastreamName}</li>;
        });

        let linkedVariablesMessage = null;
        if (linkedVariables.length > 0) {
            const messageItems = linkedVariables.map(variableName => {
                return <li key={variableName}>{variableName}</li>;
            });
            linkedVariablesMessage = (
                <div>
                    <p>All of these corresponding variables will also be deleted:</p>
                    <ul>
                        {messageItems}
                    </ul>
                </div>
            );
        }
        let linkedCoordinateSystemsMessage = null;
        if (linkedCoordinateSystems.length > 0) {
            const messageItems = linkedCoordinateSystems.map(coordinateSystemName => {
                return <li key={coordinateSystemName}>{coordinateSystemName}</li>;
            });
            linkedCoordinateSystemsMessage = (
                <div>
                    <p>The following coordinate systems will no longer be mapped:</p>
                    <ul>
                        {messageItems}
                    </ul>
                </div>
            );
        }

        this.validationResults.message = (
            <div>
                <div><p>Are you sure you want to delete these retrieval rules?</p>
                    <ul>
                        {datastreamItems}
                    </ul>
                </div>
                {linkedVariablesMessage}
                {linkedCoordinateSystemsMessage}
            </div>
        );

        return this.validationResults;
    };

    canRetrievedVariablesBeDeleted = (selectedItems) => {
        this.setDefaultDeleteValidationResults();

        const messageArray = [];
        for (const varId in selectedItems) {
            messageArray.push(
                <li key={varId}>
                    <strong>{varId}</strong>
                </li>
            );
        }

        this.validationResults.message = (
            <div><p>Are you sure you want to delete these retrieved variable(s)?</p>
                <ul>
                    {messageArray}
                </ul>
            </div>
        );

        return this.validationResults;
    };

    canCoordSystemsBeDeleted = (selectedItems) => {
        this.setDefaultDeleteValidationResults();

        const vars = this.process.variable_retrieval.retrieved_variables;
        const messageArray = [];

        for (const coordSysId in selectedItems) {
            let dependentVarsCount = 0;
            for (const variableId in vars) {
                if (vars[variableId].coordinate_system === coordSysId) {
                    dependentVarsCount++;
                }
            }
            let dependencyMessage = '';
            if (dependentVarsCount > 0) {
                dependencyMessage = `(maps to ${dependentVarsCount} variables)`;
            }
            messageArray.push(
                <li key={coordSysId}>
                    <strong>{coordSysId}</strong> {dependencyMessage}
                </li>
            );
        }

        this.validationResults.message = (
            <div><p>Are you sure you want to delete these coordinate system(s)?</p>
                <ul>
                    {messageArray}
                </ul>
            </div>
        );

        return this.validationResults;
    };

    canOutputDatastreamsBeDeleted = (selectedItems) => {
        this.setDefaultDeleteValidationResults();

        const mappings = this.process.variable_retrieval.output_datastream_variable_mappings;
        const messageArray = [];
        for (const dataStreamKey in selectedItems) {
            // give warning if stream maps to any retrieved vars but still let them do the delete (clean up will be in reducer)
            let dependencyMessage = '';
            if (mappings &&
                mappings[dataStreamKey] &&
                Object.keys(mappings[dataStreamKey]).length > 0) {
                dependencyMessage = `(maps to ${Object.keys(mappings[dataStreamKey]).length} variables)`;
            }
            messageArray.push(
                <li key={dataStreamKey}>
                    <strong>{dataStreamKey}</strong> {dependencyMessage}
                </li>);
        }

        this.validationResults.message = (
            <div><p>Are you sure you want to delete these output datastream(s)?</p>
                <ul>
                    {messageArray}
                </ul>
            </div>
        );

        return this.validationResults;
    };

    canOutputDatastreamMappingBeDeleted = (selectedItems) => {
        this.setDefaultDeleteValidationResults();
        const messageArray = [];

        for (const varId in selectedItems) {
            messageArray.push(
                <li key={varId}>
                    <strong>{varId}</strong>
                </li>
            );
        }

        this.validationResults.message = (
            <div><p>Are you sure you want to delete these variable mappings(s)?</p>
                <ul>
                    {messageArray}
                </ul>
            </div>
        );

        return this.validationResults;
    };

    isNewProcessNameValid = (name) => {
        this.validationResults = { level: null, message: '' };
        const state = ProcessService.getMyState(ProcessService.getStore().getState());
        const validNamePattern = /^[\w]*$/;

        // First check normal name validation rules
        if (name === '' || name === null) {
            this.validationResults = { level: Constants.LEVEL_ERROR, message: 'Name cannot be blank.' };
        } else if (!validNamePattern.test(name)) {
            this.validationResults = { level: Constants.LEVEL_ERROR, message: 'Name may only include letters, numbers, and underscore.' };
        } else if (name.length < 2) {
            this.validationResults = { level: Constants.LEVEL_ERROR, message: 'Name must be at least two characters long.' };
        } else {
            const processNames = state.sortedIds;
            const workingCopyNames = Object.keys(state.workingCopiesById);
            if (workingCopyNames.indexOf(name) !== -1 || processNames.indexOf(name) !== -1) {
                this.validationResults = { level: false, message: 'A process already exists with that name.' };
            }
        }

        return this.validationResults;
    }

}



// WEBPACK FOOTER //
// ./src/utils/ProcessValidator.js